import katex from 'katex';
import { findCounterArrays, renderCounterText, counterTextToSpeech } from './counterModels.js';

export const MATH_CONTENT_VERSION = 'assessnow-math-content-1.1.0';

export const MATH_CONTENT_LIMITS = Object.freeze({
  maximumContentLength: 100000,
  maximumExpressionLength: 2000,
  maximumExpressions: 100,
  maximumBraceDepth: 64
});

const OPENERS = Object.freeze([
  Object.freeze({ open: '$$', close: '$$', displayMode: true, dollar: true }),
  Object.freeze({ open: '\\(', close: '\\)', displayMode: false }),
  Object.freeze({ open: '\\[', close: '\\]', displayMode: true }),
  Object.freeze({ open: '$', close: '$', displayMode: false, dollar: true })
]);

const FORBIDDEN_TEX_COMMAND = /\\(?:href|url|includegraphics|htmlClass|htmlId|htmlStyle|htmlData|class|style|cssId|unicode|include|input|def|edef|xdef|gdef|newcommand|renewcommand|let|futurelet|catcode|write|read|openout|special)\b/i;
const HTML_TAG_LIKE = /<\/?[A-Za-z][^>]*>/;
const UNICODE_SUPERSCRIPT = Object.freeze({
  '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5',
  '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9', '⁺': '+', '⁻': '-'
});
const UNICODE_SUBSCRIPT = Object.freeze({
  '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5',
  '₆': '6', '₇': '7', '₈': '8', '₉': '9', '₊': '+', '₋': '-'
});

export function escapeMathHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function appendText(segments, value) {
  if (!value) return;
  const previous = segments[segments.length - 1];
  if (previous?.type === 'text') previous.value += value;
  else segments.push({ type: 'text', value });
}

function isEscapedAt(text, index) {
  let backslashes = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) backslashes += 1;
  return backslashes % 2 === 1;
}

function findToken(text, token, fromIndex, { singleDollar = false } = {}) {
  let index = text.indexOf(token, fromIndex);
  while (index >= 0) {
    const adjacentDollar = singleDollar && (text[index - 1] === '$' || text[index + 1] === '$');
    if (!isEscapedAt(text, index) && !adjacentDollar) return index;
    index = text.indexOf(token, index + token.length);
  }
  return -1;
}

function nextOpener(text, fromIndex) {
  let found = null;
  for (const delimiter of OPENERS) {
    const index = findToken(text, delimiter.open, fromIndex, { singleDollar: delimiter.open === '$' });
    if (index >= 0 && (!found || index < found.index
        || (index === found.index && delimiter.open.length > found.open.length))) {
      found = { ...delimiter, index };
    }
  }
  return found;
}

const MATH_WORDS = new Set([
  'sin', 'cos', 'tan', 'log', 'ln', 'lim', 'max', 'min', 'mod', 'gcd', 'lcm',
  'sqrt', 'abs', 'floor', 'ceil', 'pi', 'theta', 'alpha', 'beta', 'gamma', 'delta',
  'det', 'exp', 'inf', 'sup', 'matrix', 'pmatrix', 'bmatrix', 'vmatrix',
  'cases', 'aligned', 'array', 'gathered', 'split'
]);

const PROSE_WORDS = new Set([
  'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'did', 'do',
  'does', 'each', 'enter', 'evaluate', 'find', 'for', 'from', 'has', 'have',
  'how', 'if', 'in', 'into', 'is', 'it', 'its', 'of', 'on', 'or', 'order',
  'plot', 'select', 'show', 'solve', 'than', 'that', 'the', 'then', 'this',
  'to', 'true', 'use', 'value', 'what', 'when', 'where', 'which', 'with'
]);

function hasProseWord(value) {
  const withoutTextGroups = String(value)
    .replace(/\\(?:text|textrm|textsf|texttt|mathrm|mathbf|operatorname)\s*\{[^{}]*\}/g, ' ')
    .replace(/\\[A-Za-z]+/g, ' ');
  return (withoutTextGroups.match(/[A-Za-z]+/g) || []).some(word => {
    const normalized = word.toLowerCase();
    return !MATH_WORDS.has(normalized) && (word.length >= 3 || PROSE_WORDS.has(normalized));
  });
}

export function looksLikeStandaloneMath(value) {
  const text = String(value ?? '').trim();
  if (!text || text.length > MATH_CONTENT_LIMITS.maximumExpressionLength) return false;
  if (/\d{1,4}[/-]\d{1,2}[/-]\d{1,4}/.test(text) || /[$`?]/.test(text)) return false;
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(text) || hasProseWord(text)) return false;
  const hasCommand = /\\[A-Za-z]+/.test(text);
  const hasRelation = /(?:=|<|>|≤|≥|≠|≈|\\(?:leq?|geq?|ne(?:q)?|approx|equiv)\b)/.test(text);
  const hasOperation = /(?:[+*×÷·^_!]|[±∓√∑∏∫∞∝°∠△⊥∥≅∼∈∉⊂⊆∪∩⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]|%|(?:^|\s)[−-]\s*\d|\d\s*[−-]\s*\d)/.test(text);
  const isFraction = /^[+-]?(?:\d+\s+)?\d+[\/⁄]\d+$/.test(text);
  const isCoordinate = /^[A-Za-z]?\s*\(\s*[+-]?\d+(?:\.\d+)?\s*,\s*[+-]?\d+(?:\.\d+)?\s*\)$/.test(text);
  const isRatio = /^[+-]?\d+(?:\.\d+)?\s*:\s*[+-]?\d+(?:\.\d+)?$/.test(text);
  const isFunctionNotation = /^(?:[A-Za-z]|sin|cos|tan|log|ln|sqrt|abs|max|min)\s*\([^?]+\)$/i.test(text);
  const isAbsoluteValue = /^\|[^|]+\|$/.test(text);
  return hasCommand || hasRelation || hasOperation || isFraction || isCoordinate
    || isRatio || isFunctionNotation || isAbsoluteValue;
}

function looksLikeDollarMath(value) {
  const text = String(value ?? '').trim();
  return Boolean(text)
    && !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(text)
    && !hasProseWord(text);
}

export function parseMathContent(value) {
  const text = String(value ?? '');
  if (!text || text.length > MATH_CONTENT_LIMITS.maximumContentLength) {
    return text ? [{ type: 'text', value: text }] : [];
  }

  const segments = [];
  let cursor = 0;
  let count = 0;
  while (cursor < text.length) {
    const opener = nextOpener(text, cursor);
    if (!opener || count >= MATH_CONTENT_LIMITS.maximumExpressions) {
      appendText(segments, text.slice(cursor));
      break;
    }
    appendText(segments, text.slice(cursor, opener.index));
    const expressionStart = opener.index + opener.open.length;
    const closeIndex = findToken(text, opener.close, expressionStart, { singleDollar: opener.close === '$' });
    if (closeIndex < 0) {
      appendText(segments, text.slice(opener.index));
      break;
    }
    const source = text.slice(expressionStart, closeIndex).trim();
    if (!source || source.length > MATH_CONTENT_LIMITS.maximumExpressionLength
        || (opener.dollar && !looksLikeDollarMath(source))) {
      appendText(segments, text.slice(opener.index, closeIndex + opener.close.length));
    } else {
      segments.push({
        type: 'math',
        source,
        displayMode: opener.displayMode,
        original: text.slice(opener.index, closeIndex + opener.close.length)
      });
      count += 1;
    }
    cursor = closeIndex + opener.close.length;
  }
  if (!segments.some(segment => segment.type === 'math') && looksLikeStandaloneMath(text)) {
    const standaloneSource = text.trim();
    return [{
      type: 'math',
      source: standaloneSource,
      displayMode: /\\begin\s*\{(?:aligned|array|cases|matrix|pmatrix|bmatrix|vmatrix|gathered|split)\}/.test(text)
        || text.includes('\n')
        || /(?:=|<|>|≤|≥|≠|≈|\\(?:leq?|geq?|ne(?:q)?|approx|equiv)\b)/.test(standaloneSource),
      original: text
    }];
  }
  return segments;
}

export function containsMathContent(value) {
  return findCounterArrays(value).length > 0 || parseMathContent(value).some(segment => segment.type === 'math');
}

function balancedGroup(text, startIndex) {
  if (text[startIndex] !== '{') return null;
  let depth = 0;
  for (let index = startIndex; index < text.length; index += 1) {
    if (text[index] === '\\') {
      index += 1;
      continue;
    }
    if (text[index] === '{') depth += 1;
    if (text[index] === '}') {
      depth -= 1;
      if (depth === 0) return { value: text.slice(startIndex + 1, index), end: index + 1 };
    }
  }
  return null;
}

function replaceGroupedCommand(input, command, groupCount, replacement) {
  const marker = `\\${command}`;
  let cursor = 0;
  let result = '';
  while (cursor < input.length) {
    const markerIndex = input.indexOf(marker, cursor);
    if (markerIndex < 0) return result + input.slice(cursor);
    const afterName = markerIndex + marker.length;
    if (/[A-Za-z]/.test(input[afterName] || '')) {
      result += input.slice(cursor, afterName);
      cursor = afterName;
      continue;
    }
    let groupStart = afterName;
    const groups = [];
    while (/\s/.test(input[groupStart] || '')) groupStart += 1;
    let end = groupStart;
    for (let index = 0; index < groupCount; index += 1) {
      const group = balancedGroup(input, end);
      if (!group) break;
      groups.push(group.value);
      end = group.end;
      while (/\s/.test(input[end] || '')) end += 1;
    }
    if (groups.length !== groupCount) {
      result += input.slice(cursor, afterName);
      cursor = afterName;
      continue;
    }
    result += input.slice(cursor, markerIndex) + replacement(groups);
    cursor = end;
  }
  return result;
}

const SPOKEN_COMMANDS = Object.freeze({
  times: ' times ', cdot: ' times ', div: ' divided by ', pm: ' plus or minus ',
  mp: ' minus or plus ', le: ' less than or equal to ', leq: ' less than or equal to ',
  ge: ' greater than or equal to ', geq: ' greater than or equal to ',
  ne: ' not equal to ', neq: ' not equal to ', approx: ' approximately ',
  equiv: ' is equivalent to ', infty: ' infinity ', pi: ' pi ', theta: ' theta ',
  alpha: ' alpha ', beta: ' beta ', gamma: ' gamma ', delta: ' delta ',
  angle: ' angle ', triangle: ' triangle ', perp: ' perpendicular to ',
  parallel: ' parallel to ', cong: ' congruent to ', sim: ' similar to ',
  propto: ' proportional to ', degree: ' degrees ', sum: ' sum ', prod: ' product ',
  int: ' integral ', log: ' log ', ln: ' natural log ', sin: ' sine ', cos: ' cosine ',
  tan: ' tangent ', circ: ' degrees ', lt: ' less than ', gt: ' greater than ',
  in: ' is in ', notin: ' is not in ', subset: ' is a subset of ',
  subseteq: ' is a subset of or equal to ', cup: ' union ', cap: ' intersection ',
  forall: ' for all ', exists: ' there exists '
});

export function mathExpressionToSpeech(value) {
  let text = String(value ?? '').slice(0, MATH_CONTENT_LIMITS.maximumExpressionLength);
  for (let pass = 0; pass < 16; pass += 1) {
    const prior = text;
    text = replaceGroupedCommand(text, 'frac', 2, ([numerator, denominator]) => (
      ` ${mathExpressionToSpeech(numerator)} over ${mathExpressionToSpeech(denominator)} `
    ));
    text = replaceGroupedCommand(text, 'dfrac', 2, ([numerator, denominator]) => (
      ` ${mathExpressionToSpeech(numerator)} over ${mathExpressionToSpeech(denominator)} `
    ));
    text = replaceGroupedCommand(text, 'tfrac', 2, ([numerator, denominator]) => (
      ` ${mathExpressionToSpeech(numerator)} over ${mathExpressionToSpeech(denominator)} `
    ));
    text = replaceGroupedCommand(text, 'sqrt', 1, ([radicand]) => (
      ` square root of ${mathExpressionToSpeech(radicand)} `
    ));
    text = replaceGroupedCommand(text, 'text', 1, ([content]) => ` ${content} `);
    text = replaceGroupedCommand(text, 'mathrm', 1, ([content]) => ` ${content} `);
    text = replaceGroupedCommand(text, 'mathbf', 1, ([content]) => ` ${content} `);
    if (text === prior) break;
  }

  text = text
    .replace(/\\begin\s*\{[^{}]+\}|\\end\s*\{[^{}]+\}/g, ' ')
    .replace(/\\\\/g, '; ')
    .replace(/\^\s*\{\\circ\}/g, ' degrees ')
    .replace(/\^\s*\{?2\}?/g, ' squared ')
    .replace(/\^\s*\{?3\}?/g, ' cubed ')
    .replace(/\^\s*\{([^{}]+)\}/g, ' to the power of $1 ')
    .replace(/\^\s*([A-Za-z0-9.+-]+)/g, ' to the power of $1 ')
    .replace(/_\s*\{([^{}]+)\}/g, ' sub $1 ')
    .replace(/_\s*([A-Za-z0-9.+-]+)/g, ' sub $1 ')
    .replace(/\\left|\\right/g, ' ')
    .replace(/\\([A-Za-z]+)/g, (_, command) => SPOKEN_COMMANDS[command] || ` ${command} `)
    .replace(/\\[%$&#_{}]/g, match => match.slice(1))
    .replace(/[{}]/g, ' ')
    .replace(/\|([^|]+)\|/g, ' absolute value of $1 ')
    .replace(/[×·*]/g, ' times ')
    .replace(/[÷]/g, ' divided by ')
    .replace(/[−-]/g, ' minus ')
    .replace(/\+/g, ' plus ')
    .replace(/≤/g, ' less than or equal to ')
    .replace(/≥/g, ' greater than or equal to ')
    .replace(/[≠]/g, ' not equal to ')
    .replace(/</g, ' less than ')
    .replace(/>/g, ' greater than ')
    .replace(/=/g, ' equals ')
    .replace(/(\d+)\s*[\/⁄]\s*(\d+)/g, '$1 over $2')
    .replace(/%/g, ' percent ')
    .replace(/!/g, ' factorial ')
    .replace(/:/g, ' to ')
    .replace(/&/g, ' ')
    .replace(/\(/g, ' open parenthesis ')
    .replace(/\)/g, ' close parenthesis ')
    .replace(/\s+/g, ' ')
    .trim();
  return text;
}

export function mathContentToSpeech(value, explicitSpeech = '') {
  const override = String(explicitSpeech ?? '').trim();
  if (override) return override;
  return parseMathContent(value)
    .map(segment => segment.type === 'math' ? mathExpressionToSpeech(expressionToTex(segment.source)) : counterTextToSpeech(segment.value))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

export function expressionToTex(value) {
  let text = String(value ?? '').trim();
  if (!text) return '';

  if (!/\\[A-Za-z]+/.test(text)) {
    const mixed = text.match(/^([+-]?\d+)\s+(\d+)[\/⁄](\d+)(\s*%?)$/);
    if (mixed) text = `${mixed[1]}\\frac{${mixed[2]}}{${mixed[3]}}${mixed[4] ? '\\%' : ''}`;
    const fraction = text.match(/^([+-]?\d+)[\/⁄](\d+)(\s*%?)$/);
    if (fraction) text = `\\frac{${fraction[1]}}{${fraction[2]}}${fraction[3] ? '\\%' : ''}`;
  }

  return text
    .replace(/[−–—]/g, '-')
    .replace(/½/g, '\\frac{1}{2}')
    .replace(/⅓/g, '\\frac{1}{3}')
    .replace(/⅔/g, '\\frac{2}{3}')
    .replace(/¼/g, '\\frac{1}{4}')
    .replace(/¾/g, '\\frac{3}{4}')
    .replace(/⅕/g, '\\frac{1}{5}')
    .replace(/⅖/g, '\\frac{2}{5}')
    .replace(/⅗/g, '\\frac{3}{5}')
    .replace(/⅘/g, '\\frac{4}{5}')
    .replace(/⅙/g, '\\frac{1}{6}')
    .replace(/⅚/g, '\\frac{5}{6}')
    .replace(/⅛/g, '\\frac{1}{8}')
    .replace(/⅜/g, '\\frac{3}{8}')
    .replace(/⅝/g, '\\frac{5}{8}')
    .replace(/⅞/g, '\\frac{7}{8}')
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻]+/g, run => `^{${[...run].map(character => UNICODE_SUPERSCRIPT[character]).join('')}}`)
    .replace(/[₀₁₂₃₄₅₆₇₈₉₊₋]+/g, run => `_{${[...run].map(character => UNICODE_SUBSCRIPT[character]).join('')}}`)
    .replace(/(^|[^\d.])([+-]?\d+)[\/⁄](\d+)(?=$|[^\d.])/g, (_, prefix, numerator, denominator) => (
      `${prefix}\\frac{${numerator}}{${denominator}}`
    ))
    .replace(/≤|<=/g, '\\le ')
    .replace(/≥|>=/g, '\\ge ')
    .replace(/≠|!=/g, '\\ne ')
    .replace(/≈/g, '\\approx ')
    .replace(/≡/g, '\\equiv ')
    .replace(/±/g, '\\pm ')
    .replace(/∓/g, '\\mp ')
    .replace(/[×·*]/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/∞/g, '\\infty ')
    .replace(/∝/g, '\\propto ')
    .replace(/∑/g, '\\sum ')
    .replace(/∏/g, '\\prod ')
    .replace(/∫/g, '\\int ')
    .replace(/π/g, '\\pi ')
    .replace(/θ/g, '\\theta ')
    .replace(/α/g, '\\alpha ')
    .replace(/β/g, '\\beta ')
    .replace(/γ/g, '\\gamma ')
    .replace(/δ/g, '\\delta ')
    .replace(/∠/g, '\\angle ')
    .replace(/△/g, '\\triangle ')
    .replace(/⊥/g, '\\perp ')
    .replace(/∥/g, '\\parallel ')
    .replace(/≅/g, '\\cong ')
    .replace(/∼/g, '\\sim ')
    .replace(/∈/g, '\\in ')
    .replace(/∉/g, '\\notin ')
    .replace(/⊆/g, '\\subseteq ')
    .replace(/⊂/g, '\\subset ')
    .replace(/∪/g, '\\cup ')
    .replace(/∩/g, '\\cap ')
    .replace(/°/g, '^{\\circ}')
    .replace(/\bsqrt\s*\(([^()]*)\)/gi, '\\sqrt{$1}')
    .replace(/\babs\s*\(([^()]*)\)/gi, '\\left|$1\\right|')
    .replace(/√\s*\(([^()]*)\)/g, '\\sqrt{$1}')
    .replace(/√\s*([A-Za-z0-9.]+)/g, '\\sqrt{$1}')
    .replace(/(?<!\\)%/g, '\\%');
}

export function renderMathExpressionToHtml(value, { displayMode = false } = {}) {
  const original = String(value ?? '').trim();
  const source = expressionToTex(original);
  if (!source || source.length > MATH_CONTENT_LIMITS.maximumExpressionLength
      || HTML_TAG_LIKE.test(source) || FORBIDDEN_TEX_COMMAND.test(source)) {
    return `<span class="math-render-fallback" data-math-render-error="true">${escapeMathHtml(original)}</span>`;
  }
  try {
    return katex.renderToString(source, {
      displayMode,
      throwOnError: true,
      strict: 'error',
      trust: false,
      output: 'htmlAndMathml',
      maxSize: 20,
      maxExpand: 100
    });
  } catch {
    return `<span class="math-render-fallback" data-math-render-error="true">${escapeMathHtml(original)}</span>`;
  }
}

export function renderMathContentToHtml(value) {
  return parseMathContent(value).map(segment => {
    if (segment.type === 'text') return renderCounterText(segment.value);
    const speech = escapeMathHtml(mathExpressionToSpeech(expressionToTex(segment.source)));
    return `<span class="assessnow-math${segment.displayMode ? ' assessnow-math-display' : ' assessnow-math-inline'}" role="math" aria-label="${speech}">${renderMathExpressionToHtml(segment.source, { displayMode: segment.displayMode })}</span>`;
  }).join('');
}

export function renderRawMathExpressionToHtml(value, options = {}) {
  const tex = expressionToTex(value);
  const speech = escapeMathHtml(mathExpressionToSpeech(tex));
  const displayMode = Boolean(options.displayMode);
  return `<span class="assessnow-math ${displayMode ? 'assessnow-math-display' : 'assessnow-math-inline'}" role="math" aria-label="${speech}">${renderMathExpressionToHtml(tex, options)}</span>`;
}
